import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Empty, Modal, Spin, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { MdAccountBalance } from 'react-icons/md';
import axios from '../config/axios';
import { AuthContext } from '../context/auth.context';
import { PaymentAccount } from '../types/payment-account.types';
import { handleAPIError } from '../utils/helpers';

interface RetailerPaymentAccountsProps {
	open: boolean;
	onClose: () => void;
	retailerId: number;
	retailerName: string;
	onCreatePaymentAccount?: () => void;
}

const RetailerPaymentAccounts = ({ open, onClose, retailerId, retailerName, onCreatePaymentAccount }: RetailerPaymentAccountsProps) => {
	const { user } = useContext(AuthContext);
	const queryClient = useQueryClient();

	const { mutate: deleteAccount, isPending: isDeleting } = useMutation({
		mutationFn: async (id: number) => {
			const response = await axios.delete(
				`/admin/retailers/payment-accounts/${id}/delete`,
				{ withCredentials: true },
			);
			return response.data;
		},
		onSuccess: () => {
			message.success('Payment account deleted');
			queryClient.invalidateQueries({ queryKey: ['retailer-payment-accounts'] });
		},
		onError: handleAPIError,
	});

	const confirmDelete = (account: PaymentAccount) => {
		Modal.confirm({
			title: 'Delete this payment account?',
			content:
				'An account that has already been paid through will be refused — deactivate that instead.',
			okText: 'Delete',
			okButtonProps: { danger: true },
			onOk: () => deleteAccount(account.id),
		});
	};

	const {
		data: paymentAccounts,
		isLoading,
		error,
	} = useQuery({
		queryKey: ['retailer-payment-accounts', retailerId],
		queryFn: async () => {
			const response = await axios.get(`/admin/retailers/${retailerId}/payment-accounts`, {
				withCredentials: true,
			});
			return response.data.results;
		},
		enabled: open && !!retailerId,
	});

	if (error) {
		handleAPIError(error);
	}

	const columns: ColumnsType<PaymentAccount> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 80,
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			render: (type: string) => <Tag color={type === 'bank' ? 'blue' : 'green'}>{type === 'bank' ? 'Bank' : 'Mobile Money'}</Tag>,
		},
		{
			title: 'Account Details',
			key: 'id',
			render: (_, record: PaymentAccount) => (
				<div className="space-y-1">
					<div>
						<Typography.Text strong>Account Number:</Typography.Text> <Typography.Text>{record.account_number}</Typography.Text>
					</div>
					{record.account_name && (
						<div>
							<Typography.Text strong>Account Name:</Typography.Text> <Typography.Text>{record.account_name}</Typography.Text>
						</div>
					)}
					<div>
						<Typography.Text strong>Bank:</Typography.Text> <Typography.Text>{record.bank_name}</Typography.Text>
					</div>
					{record.branch_name && (
						<div>
							<Typography.Text strong>Branch:</Typography.Text> <Typography.Text>{record.branch_name}</Typography.Text>
						</div>
					)}
				</div>
			),
		},
		{
			title: 'Status',
			dataIndex: 'is_active',
			key: 'is_active',
			render: (is_active: boolean) => <Tag color={is_active ? 'green' : 'red'}>{is_active ? 'Active' : 'Inactive'}</Tag>,
		},
		{
			title: 'Created Date',
			dataIndex: 'created_at',
			key: 'created_at',
			render: (created_at: Date) => <Typography.Text>{dayjs(created_at).format('DD MMM YYYY - LT')}</Typography.Text>,
		},
	];

	if (user?.is_superuser) {
		columns.push({
			title: '',
			key: 'delete',
			render: (_, record: PaymentAccount) => (
				<Button size="small" danger loading={isDeleting} onClick={() => confirmDelete(record)}>
					Delete
				</Button>
			),
		});
	}

	return (
		<Modal
			title={
				<div>
					<Typography.Title
						level={4}
						className="mb-0"
					>
						Payment Accounts
					</Typography.Title>
					<Typography.Text type="secondary">
						{retailerName} (ID: {retailerId})
					</Typography.Text>
				</div>
			}
			open={open}
			onCancel={onClose}
			footer={null}
			width={1000}
			style={{ top: 20 }}
		>
			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Spin size="large" />
				</div>
			) : paymentAccounts?.length === 0 ? (
				<Empty
					description={
						<div className="text-center">
							<p className="mb-4 text-gray-500">No payment accounts found for this retailer</p>
							{onCreatePaymentAccount && (
								<Button
									type="primary"
									icon={<MdAccountBalance />}
									onClick={onCreatePaymentAccount}
								>
									Create Payment Account
								</Button>
							)}
						</div>
					}
					className="py-8"
				/>
			) : (
				<Table
					columns={columns}
					dataSource={paymentAccounts}
					rowKey="id"
					pagination={false}
					size="small"
					scroll={{ x: 800 }}
				/>
			)}
		</Modal>
	);
};

export default RetailerPaymentAccounts;
